import type { AttendanceCorrection, AttendanceDay } from '../api/types';
import type { LeaveType } from './correction-form';

/**
 * Jatah cuti dan pemakaiannya — MURNI, tanpa React dan tanpa jam sistem.
 *
 * Ketetapan Owner 21 Agu 2026: **maksimal 14 hari, per tahun kalender.**
 *
 * Modul ini MENGHITUNG dan MENAMPILKAN, tidak menegakkan — juga itu ketetapan Owner. Layar
 * memberi tahu, Owner yang memutuskan. Menolak pengajuan ke-15 menuntut jawaban atas kasus tepi
 * yang belum pernah ditanyakan (cuti sakit ikut dihitung atau tidak, pengecualian oleh atasan,
 * nasib pengajuan yang ditolak) — dan aturan yang ditegakkan salah lebih mahal daripada aturan
 * yang hanya ditampilkan.
 *
 * Tidak ada tabel jatah cuti, dan tidak dibuat: hari cuti yang disetujui SUDAH tercatat sebagai
 * `AttendanceDay` berstatus `ON_LEAVE`. Menghitungnya dari sana berarti nol migrasi dan nol
 * sumber kebenaran kedua yang bisa berbeda dari yang pertama.
 */

/** Ketetapan Owner 21 Agu 2026. Angka, bukan kebijakan per organisasi — belum ada tempatnya. */
export const LEAVE_LIMIT_DAYS = 14;

/**
 * Jenis izin yang MEMAKAN jatah — ketetapan Owner 24 Agu 2026: "Sakit tidak, izin pribadi ya."
 *
 * `MATERNITY` ikut dikecualikan meski tidak disebut eksplisit dalam ketetapan itu: cuti
 * melahirkan berlangsung jauh lebih lama daripada jatah 14 hari/tahun ini, dan memakannya dari
 * bucket yang sama akan membuat orang yang melahirkan kehabisan cuti tahunannya karena
 * melahirkan — konsekuensi yang rasanya tidak mungkin dimaksud siapa pun.
 *
 * `null`/`undefined` dihitung SETARA `ANNUAL` — makna "cuti" sebelum kategori ini ada. Baris
 * lama (dan pengajuan yang lolos tanpa jenis sebelum validasi ini ditegakkan server) tidak
 * boleh diam-diam berhenti memakan jatah hanya karena kolomnya belum pernah diisi.
 */
export function countsTowardQuota(leaveType: LeaveType | string | null | undefined): boolean {
  return leaveType !== 'SICK' && leaveType !== 'MATERNITY';
}

export function yearOf(date: string): string {
  return date.slice(0, 4);
}

/**
 * Rentang tahun kalender yang memuat `today`.
 *
 * Sampai 31 Desember, bukan sampai hari ini: cuti dapat ditandai untuk tanggal yang sudah lewat
 * MAUPUN hari ini, dan membatasi `to` pada hari ini akan menjatuhkan hari yang baru saja disetujui
 * di zona waktu yang lebih dulu berganti tanggal.
 */
export function calendarYearRange(today: string): { readonly from: string; readonly to: string } {
  const tahun = yearOf(today);
  return { from: `${tahun}-01-01`, to: `${tahun}-12-31` };
}

export interface LeaveUsage {
  readonly year: string;
  /** Hari yang sudah DISETUJUI — berstatus `ON_LEAVE`. */
  readonly approved: number;
  /** Hari yang diajukan dan belum diputus. Dihitung terhadap sisa, tetapi ditampilkan terpisah. */
  readonly pending: number;
  readonly limit: number;
  readonly remaining: number;
  readonly overLimit: boolean;
}

/**
 * Menghitung pemakaian cuti pada tahun yang memuat `today`.
 *
 * Tiga keputusan yang layak disebut:
 *
 * 1. **HARI, bukan pengajuan.** Tanggal yang sama dihitung sekali walau datanya muncul berkali-kali.
 *    Satu pengajuan koreksi = satu tanggal, jadi tanpa dedupe, data yang terduplikasi karena
 *    pengajuan ulang akan memakan jatah orang yang tidak pernah cuti dua kali di hari yang sama.
 *
 * 2. **Yang MENUNGGU ikut mengurangi sisa.** Kalau tidak, orang dengan 13 hari terpakai dan 3
 *    pengajuan menggantung akan membaca "sisa 1" lalu mengajukan lagi. Ditampilkan terpisah supaya
 *    ia tetap dapat membedakan yang sudah pasti dari yang belum.
 *
 * 3. **Tanggal yang sudah disetujui tidak dihitung dua kali**, walau pengajuannya masih tercatat
 *    PENDING karena alasan apa pun.
 */
/**
 * Bobot izin SETENGAH hari terhadap jatah — ketetapan Owner 24 Agu 2026: 0,5 hari, bukan 0
 * (kelonggaran operasional) dan bukan 1 (disamakan cuti penuh). Dua izin setengah hari di
 * tanggal berbeda memakan jatah sama besar dengan satu hari cuti penuh.
 */
const HALF_DAY_WEIGHT = 0.5;

/**
 * `corrections` yang dianggap "MENUNGGU/DISETUJUI, jadi ikut dihitung" — SEMUA status KECUALI
 * yang sudah pasti tidak berlaku. Beda dari batasan lama (implisit hanya PENDING): sejak izin
 * setengah hari ada, "disetujui" untuk tipe itu TIDAK PERNAH tercatat di `AttendanceDay` —
 * `MARK_HALF_DAY_LEAVE` sengaja tidak menyentuh `attendance_days` sama sekali (lihat komentar
 * enum di server), jadi satu-satunya sumber kebenarannya adalah baris koreksi itu sendiri.
 * Pemanggil karena itu WAJIB mengirim seluruh koreksi tahun ini, bukan hanya yang PENDING.
 */
function ikutDihitung(status: string): boolean {
  return status === 'PENDING' || status === 'APPROVED';
}

export function summarizeLeave(
  days: readonly AttendanceDay[],
  corrections: readonly AttendanceCorrection[],
  today: string,
  limit: number = LEAVE_LIMIT_DAYS,
): LeaveUsage {
  const tahun = yearOf(today);
  const diTahunIni = (tanggal: unknown): tanggal is string =>
    typeof tanggal === 'string' && yearOf(tanggal) === tahun;

  const disetujui = new Set<string>();
  for (const hari of days) {
    if (hari?.status !== 'ON_LEAVE') continue;
    if (!diTahunIni(hari.workDate)) continue;
    if (!countsTowardQuota(hari.leaveType)) continue;
    disetujui.add(hari.workDate);
  }

  const menunggu = new Set<string>();
  const setengahDisetujui = new Set<string>();
  const setengahMenunggu = new Set<string>();
  for (const koreksi of corrections) {
    if (!diTahunIni(koreksi?.workDate)) continue;

    if (koreksi.correctionType === 'MARK_ON_LEAVE') {
      // Yang APPROVED sudah terhitung lewat `days` di atas — hanya PENDING yang relevan
      // di sini, dan hanya bila tanggalnya belum disetujui.
      if (koreksi.status !== 'PENDING') continue;
      if (disetujui.has(koreksi.workDate)) continue;
      const jenis = koreksi.requestedPayload?.leaveType;
      if (!countsTowardQuota(typeof jenis === 'string' ? jenis : null)) continue;
      menunggu.add(koreksi.workDate);
      continue;
    }

    if (koreksi.correctionType === 'MARK_HALF_DAY_LEAVE') {
      // Tidak ada penyaringan jenis di sini — SETIAP izin setengah hari memakan 0,5 hari,
      // tidak ada varian yang dikecualikan (beda dari MARK_ON_LEAVE yang punya SICK/MATERNITY).
      if (!ikutDihitung(koreksi.status)) continue;
      if (koreksi.status === 'APPROVED') {
        setengahDisetujui.add(koreksi.workDate);
      } else if (!setengahDisetujui.has(koreksi.workDate)) {
        setengahMenunggu.add(koreksi.workDate);
      }
    }
  }

  const approved = disetujui.size + setengahDisetujui.size * HALF_DAY_WEIGHT;
  const pending = menunggu.size + setengahMenunggu.size * HALF_DAY_WEIGHT;
  return {
    year: tahun,
    approved,
    pending,
    limit,
    remaining: Math.max(0, limit - approved - pending),
    overLimit: approved + pending > limit,
  };
}

/**
 * Angka jatah cuti sebagai teks — bulat ditulis bulat, pecahan ditulis SATU desimal.
 *
 * `8.5` bisa muncul nyata sejak izin setengah hari ada; `8.5000000000001` tidak boleh pernah
 * muncul walau seluruh aritmetika di atas hanya menjumlah kelipatan 0,5 yang aman di floating
 * point — angka ini yang dibaca orang, jadi dijamin di titik tampil, bukan dipercaya dari hulu.
 */
export function formatDays(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

/** Kalimat yang dibaca orangnya. Angka besar ditulis lengkap — layar ini dibaca sambil berdiri. */
export function describeLeaveUsage(usage: LeaveUsage): string {
  const dasar = `${formatDays(usage.approved)} dari ${usage.limit} hari terpakai pada ${usage.year}`;
  if (usage.pending > 0) {
    return `${dasar} · ${formatDays(usage.pending)} hari menunggu persetujuan`;
  }
  return dasar;
}
